import { createStyles, Skeleton, Stack, UnstyledButton } from '@mantine/core';
import { useApiGetPromptCompletions } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { useKeyPress } from 'ahooks';
import { useState } from 'react';
import type { PromptSuggestion } from '../../../components/Search/constants';
import { NAVIGATION_SHORTCUTS } from '../../../constants/shortcuts';

function formatPromptCompletion(
	completion: string,
	selectedSuggestion: PromptSuggestion | null
) {
	if (!selectedSuggestion) {
		return <Text size="sm">completion</Text>;
	}

	const prefix = selectedSuggestion.inputPrefill;

	if (completion.startsWith(prefix)) {
		return (
			<Text size="sm">
				<strong>{prefix}</strong> {completion.substring(prefix.length)}
			</Text>
		);
	}

	return <Text size="sm">completion</Text>;
}

const useStyles = createStyles((theme) => ({
	completionItem: {
		padding: theme.spacing.xs,
		borderRadius: theme.radius.sm,
		cursor: 'pointer',
		transition: 'opacity 0.2s',
		'&:hover, &[data-selected]': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
	},
}));

interface AIPromptSuggestionsProps {
	searchTerm: string;
	suggestion: PromptSuggestion;
	onSelect: (prompt: string) => void;
}

export function AIPromptSuggestions({
	searchTerm,
	suggestion,
	onSelect,
}: AIPromptSuggestionsProps) {
	const [selectedIndex, setSelectedIndex] = useState(0);

	const { classes } = useStyles();
	const { data: promptCompletions, isLoading } = useApiGetPromptCompletions(
		{
			queryParams: {
				q: suggestion.prompt ?? '',
			},
		},
		{
			enabled: !!suggestion.prompt,
		}
	);

	useKeyPress(NAVIGATION_SHORTCUTS.ARROW_DOWN, (e) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedIndex((prev) => {
			if (prev < (promptCompletions?.completions.length ?? 0) - 1) {
				return prev + 1;
			}
			return 0;
		});
	});

	useKeyPress(NAVIGATION_SHORTCUTS.ARROW_UP, (e) => {
		e.preventDefault();
		e.stopPropagation();
		setSelectedIndex((prev) => {
			if (prev > 0) {
				return prev - 1;
			}
			return (promptCompletions?.completions.length ?? 0) - 1;
		});
	});

	useKeyPress(
		NAVIGATION_SHORTCUTS.CONFIRM_SELECTION,
		(e) => {
			e.preventDefault();
			e.stopPropagation();
			const basePrefill = (suggestion.inputPrefill ?? '').trim();
			const currentInput = searchTerm.trim();
			const isCustom = currentInput !== basePrefill;

			if (isCustom) {
				onSelect(searchTerm);
				return;
			}

			const selectedAction = promptCompletions?.completions[selectedIndex];
			if (selectedAction) {
				onSelect(selectedAction);
			} else {
				// Fallback: no completion available; use the current (prefill) input
				onSelect(searchTerm);
			}
		},
		{ useCapture: true }
	);

	return (
		<Stack spacing={0} p="xs">
			{isLoading ? (
				<Stack spacing="xs">
					{Array.from({ length: 3 }).map((_, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<Skeleton key={index} height={28} width="100%" />
					))}
				</Stack>
			) : (
				promptCompletions?.completions.map((completion, index) => (
					<UnstyledButton
						// eslint-disable-next-line react/no-array-index-key
						key={index}
						className={classes.completionItem}
						onClick={() => onSelect(completion)}
						onMouseOver={() => setSelectedIndex(index)}
						data-selected={index === selectedIndex ? 'true' : undefined}
					>
						{formatPromptCompletion(completion, suggestion)}
					</UnstyledButton>
				))
			)}
		</Stack>
	);
}
